type Priority = 'high' | 'normal' | 'low';

interface BlogConfig {
  blogHandle: string;
  templateKey: string;
  sectionId: string;
  perPage: number | string;
  paginationType: 'pagination' | 'load_more' | 'infinite_scroll';
  exceptionIds: string[];
}

interface Article {
  id: string | number;
  priority?: Priority;
  html?: string;
}

interface BlogResponse {
  paginateUrl: string | 'false';
  articles: Article[];
}

interface PaginationPart {
  title: number | string;
  is_link: boolean;
  current: boolean;
}

interface CustomBlogListSection extends HTMLElement {
  dataset: DOMStringMap & { _cblInit?: string };
}

const PRIORITY_ORDER: Record<Priority, number> = { high: 0, normal: 1, low: 2 };
const MAX_FETCH_PAGES: number = 50;

const CARET_SVG: string =
  '<svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg"' +
  ' role="presentation" class="icon icon-caret-pagination" aria-hidden="true" focusable="false">' +
  '<path d="M1 1L8 8L1 15" stroke="currentColor" stroke-width="0.75"/></svg>';

const init = (sectionEl: CustomBlogListSection | null): void => {
  if (!sectionEl || sectionEl.dataset._cblInit === 'true') return;
  sectionEl.dataset._cblInit = 'true';

  const configEl: HTMLElement | null = sectionEl.querySelector('[data-blog-config]');
  if (!configEl) return;

  let config: BlogConfig;
  try {
    config = JSON.parse(configEl.textContent || '') as BlogConfig;
  } catch (err) {
    console.error('Custom Blog List: invalid config JSON', err);
    return;
  }

  const grid: HTMLElement | null = sectionEl.querySelector('[data-blog-grid]');
  const errorEl: HTMLElement | null = sectionEl.querySelector('[data-blog-error]');
  const sentinel: HTMLElement | null = sectionEl.querySelector('[data-blog-sentinel]');
  const loadMoreWrap: HTMLElement | null = sectionEl.querySelector('[data-blog-load-more]');
  const loadMoreBtn: HTMLButtonElement | null = sectionEl.querySelector('[data-blog-load-more-btn]');
  const paginationEl: HTMLElement | null = sectionEl.querySelector('[data-blog-pagination]');

  if (!grid) return;

  const perPage: number = parseInt(String(config.perPage), 10) || 12;
  const paginationType: BlogConfig['paginationType'] = config.paginationType || 'infinite_scroll';
  const hashKey: string = 'cbl-' + (config.sectionId || '');
  const exceptionIds: string[] = (config.exceptionIds || [])
    .map((id: string): string => String(id).trim())
    .filter(Boolean);

  let allArticles: Article[] = [];
  let renderedCount: number = 0;
  let currentPage: number = 1;
  let observer: IntersectionObserver | null = null;
  let skeletonsCleared: boolean = false;

  const clearSkeletons = (): void => {
    if (skeletonsCleared) return;
    grid.querySelectorAll<HTMLElement>('[data-blog-skeleton]').forEach((el: HTMLElement): void => el.remove());
    skeletonsCleared = true;
  };

  const showError = (): void => {
    clearSkeletons();
    if (errorEl) errorEl.hidden = false;
  };

  const fetchAll = (url: string, depth: number = 0): Promise<void> => {
    if (depth >= MAX_FETCH_PAGES) {
      console.warn(`Custom Blog List: hit MAX_FETCH_PAGES (${MAX_FETCH_PAGES}); stopping pagination.`);
      return Promise.resolve();
    }
    return fetch(url, { credentials: 'same-origin' })
      .then((res: Response): Promise<BlogResponse> => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: BlogResponse): Promise<void> | void => {
        if (!data || !Array.isArray(data.articles)) return;
        allArticles = allArticles.concat(data.articles);
        const next: string | undefined = data.paginateUrl;
        if (next && next !== 'false' && next !== url) return fetchAll(next, depth + 1);
      });
  };

  const sortByPriority = (items: Article[]): Article[] => {
    return items.slice().sort((a: Article, b: Article): number => {
      const pa: number = a.priority != null && PRIORITY_ORDER[a.priority] != null ? PRIORITY_ORDER[a.priority] : 1;
      const pb: number = b.priority != null && PRIORITY_ORDER[b.priority] != null ? PRIORITY_ORDER[b.priority] : 1;
      return pa - pb;
    });
  };

  // article.html is server-rendered by Shopify (templates/blog.articles-json.liquid),
  // so the markup is already escaped by the theme's own filters. We trust it here
  // and inject via innerHTML — DO NOT pass user-controlled strings through this path.
  const articlesHTML = (slice: Article[]): string => {
    return slice
      .map((a: Article): string => `<li class="border-item"><div class="article-cards">${a.html || ''}</div></li>`)
      .join('');
  };

  const appendBatch = (): void => {
    const slice: Article[] = allArticles.slice(renderedCount, renderedCount + perPage);
    if (slice.length === 0) return;
    clearSkeletons();
    grid.insertAdjacentHTML('beforeend', articlesHTML(slice));
    renderedCount += slice.length;
  };

  const hideSentinel = (): void => {
    if (sentinel) sentinel.hidden = true;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const setupInfiniteScroll = (): void => {
    if (renderedCount >= allArticles.length) {
      hideSentinel();
      return;
    }
    if (!sentinel) return;
    sentinel.hidden = false;

    if (!('IntersectionObserver' in window)) {
      while (renderedCount < allArticles.length) appendBatch();
      hideSentinel();
      return;
    }

    observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry: IntersectionObserverEntry): void => {
          if (!entry.isIntersecting) return;
          appendBatch();
          if (renderedCount >= allArticles.length) hideSentinel();
        });
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(sentinel);
  };

  const setupLoadMore = (): void => {
    if (!loadMoreWrap || !loadMoreBtn) return;
    if (renderedCount >= allArticles.length) {
      loadMoreWrap.hidden = true;
      return;
    }
    loadMoreWrap.hidden = false;
    // appendBatch is synchronous (renders pre-fetched HTML), so a "loading" flag
    // would never paint between set and unset. Just disable to block double-clicks.
    loadMoreBtn.addEventListener('click', (): void => {
      loadMoreBtn.disabled = true;
      appendBatch();
      if (renderedCount >= allArticles.length) {
        loadMoreWrap.hidden = true;
      } else {
        loadMoreBtn.disabled = false;
      }
    });
  };

  const paginationParts = (current: number, total: number): PaginationPart[] => {
    const parts: PaginationPart[] = [];
    const window_: number = 2;
    let pages: number[] = [];

    pages.push(1);
    for (let i: number = current - window_; i <= current + window_; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (total > 1) pages.push(total);

    pages = pages
      .filter((n: number, idx: number, arr: number[]): boolean => arr.indexOf(n) === idx)
      .sort((a: number, b: number): number => a - b);

    let prev: number = 0;
    pages.forEach((n: number): void => {
      if (prev && n - prev > 1) {
        parts.push({ title: '…', is_link: false, current: false });
      }
      parts.push({ title: n, is_link: n !== current, current: n === current });
      prev = n;
    });
    return parts;
  };

  const readHashPage = (): number => {
    if (!window.location.hash) return 1;
    const match: RegExpMatchArray | null = window.location.hash.match(new RegExp('(?:^|&)' + hashKey + '=(\\d+)'));
    return match ? parseInt(match[1], 10) || 1 : 1;
  };

  const updateHashPage = (page: number): void => {
    let hash: string = window.location.hash.replace(/^#/, '');
    const re: RegExp = new RegExp('(?:^|&)' + hashKey + '=\\d+');
    hash = hash.replace(re, '').replace(/^&/, '');
    if (page > 1) {
      hash = hash ? hash + '&' + hashKey + '=' + page : hashKey + '=' + page;
    }
    const newHash: string = hash ? '#' + hash : ' ';
    history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
  };

  const scrollToTop = (): void => {
    const target: Element = sectionEl.closest('.section-custom-blog-list') || sectionEl;
    const header: Element | null = document.querySelector('sticky-header, .header-wrapper, .shopify-section-header');
    const headerOffset: number = header ? header.getBoundingClientRect().height : 0;
    const rect: DOMRect = target.getBoundingClientRect();
    const top: number = rect.top + window.pageYOffset - headerOffset - 24;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const renderPagination = (): void => {
    if (!paginationEl) return;
    const totalPages: number = Math.max(1, Math.ceil(allArticles.length / perPage));
    if (totalPages <= 1) {
      paginationEl.innerHTML = '';
      return;
    }

    let html: string = '';
    html += '<nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul class="pagination__list list-unstyled" role="list">';

    if (currentPage > 1) {
      html +=
        '<li><button type="button" class="pagination__item--prev pagination__item-arrow link motion-reduce"' +
        ' data-cbl-page="' +
        (currentPage - 1) +
        '" aria-label="Previous page"><span>' +
        CARET_SVG +
        '</span></button></li>';
    }

    paginationParts(currentPage, totalPages).forEach((part: PaginationPart): void => {
      if (part.is_link) {
        html +=
          '<li><button type="button" class="pagination__item link" data-cbl-page="' +
          part.title +
          '" aria-label="Page ' +
          part.title +
          '"><span>' +
          part.title +
          '</span></button></li>';
      } else if (part.current) {
        html +=
          '<li><span class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ' +
          part.title +
          '">' +
          part.title +
          '</span></li>';
      } else {
        html += '<li><span class="pagination__item">' + part.title + '</span></li>';
      }
    });

    if (currentPage < totalPages) {
      html +=
        '<li><button type="button" class="pagination__item--next pagination__item-arrow link motion-reduce"' +
        ' data-cbl-page="' +
        (currentPage + 1) +
        '" aria-label="Next page"><span>' +
        CARET_SVG +
        '</span></button></li>';
    }

    html += '</ul></nav>';
    paginationEl.innerHTML = html;

    paginationEl.querySelectorAll<HTMLButtonElement>('[data-cbl-page]').forEach((btn: HTMLButtonElement): void => {
      btn.addEventListener('click', (): void => {
        const page: number = parseInt(btn.getAttribute('data-cbl-page') || '', 10);
        if (!page) return;
        renderPage(page);
        updateHashPage(page);
        scrollToTop();
      });
    });
  };

  const renderPage = (page: number): void => {
    const totalPages: number = Math.max(1, Math.ceil(allArticles.length / perPage));
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    currentPage = page;
    const start: number = (page - 1) * perPage;
    const slice: Article[] = allArticles.slice(start, start + perPage);
    clearSkeletons();
    grid.innerHTML = articlesHTML(slice);
    renderedCount = start + slice.length;
    renderPagination();
  };

  const startUrl: string = '/blogs/' + config.blogHandle + '?view=' + config.templateKey;

  fetchAll(startUrl)
    .then((): void => {
      if (exceptionIds.length) {
        allArticles = allArticles.filter((a: Article): boolean => exceptionIds.indexOf(String(a.id)) === -1);
      }
      allArticles = sortByPriority(allArticles);

      if (allArticles.length === 0) {
        clearSkeletons();
        return;
      }

      if (paginationType === 'pagination') {
        renderPage(readHashPage());
      } else if (paginationType === 'load_more') {
        appendBatch();
        setupLoadMore();
      } else {
        appendBatch();
        setupInfiniteScroll();
      }
    })
    .catch((err: unknown): void => {
      console.error('Custom Blog List fetch error:', err);
      showError();
    });
};

let sectionLoadBound: boolean = false;

const customBlog = (): void => {
  document.querySelectorAll<CustomBlogListSection>('[data-blog-list-section]').forEach(init);

  if (sectionLoadBound) return;
  sectionLoadBound = true;
  document.addEventListener('shopify:section:load', (event: Event): void => {
    const target: Element | null = (event.target as Element | null);
    if (!target) return;
    const section: CustomBlogListSection | null = target.querySelector<CustomBlogListSection>('[data-blog-list-section]');
    if (section) init(section);
  });
};

export default customBlog;
